import { useCallback, useEffect, useState } from 'react'
import { supabase } from './supabase'

const LOOKBACK_FIRST_RUN_DAYS = 14
const AGENT_ANALYZER = 'basecamp_data_analyzer'
const AGENT_TASK_CREATOR = 'basecamp_task_creator'
const FAILED_LOOKBACK_MS = 24 * 60 * 60 * 1000

/**
 * @param {string | null} watermark
 */
function sinceIsoForWatermark(watermark) {
  if (watermark) return watermark
  return new Date(Date.now() - LOOKBACK_FIRST_RUN_DAYS * 86400000).toISOString()
}

/**
 * @param {string} accountId
 * @param {number} projectId
 * @param {string | null} watermark
 */
async function linkHasDelta(accountId, projectId, watermark) {
  const sinceIso = sinceIsoForWatermark(watermark)

  let recQ = supabase
    .from('basecamp_recordings')
    .select('id')
    .eq('basecamp_account_id', accountId)
    .eq('basecamp_project_id', projectId)
    .limit(1)

  recQ = watermark ? recQ.gt('updated_at_bc', watermark) : recQ.gte('updated_at_bc', sinceIso)

  const { data: recs, error: recErr } = await recQ
  if (recErr) throw recErr
  if (recs?.length) return true

  let comQ = supabase
    .from('basecamp_recording_comments')
    .select('id')
    .eq('basecamp_account_id', accountId)
    .eq('basecamp_project_id', projectId)
    .limit(1)

  comQ = watermark
    ? comQ.or(`posted_at.gt.${watermark},fetched_at.gt.${watermark}`)
    : comQ.or(`posted_at.gte.${sinceIso},fetched_at.gte.${sinceIso}`)

  const { data: comments, error: cErr } = await comQ
  if (cErr) throw cErr
  return (comments?.length ?? 0) > 0
}

const EMPTY = {
  loading: false,
  linksWithNewDataCount: 0,
  digestEnabledCount: 0,
  syncPending: false,
  badgesByAgentKey: {},
  statusHintByAgentKey: {},
  summaryLine: '',
}

/**
 * @param {string[]} badges
 * @param {Record<string, { status?: string; finished_at?: string | null } | undefined>} lastByAgent
 * @param {string} agentKey
 * @param {number} linksWithNewDataCount
 * @param {boolean} syncPending
 */
function buildStatusHint(agentKey, badges, lastByAgent, linksWithNewDataCount, syncPending) {
  if (badges.includes('inactive')) return 'This agent is disabled in settings.'
  if (badges.includes('failed')) {
    const t = lastByAgent[agentKey]?.finished_at
    return t ? `Last run failed · ${new Date(t).toLocaleString()}` : 'Last run failed — check Agents Logs.'
  }
  if (agentKey === AGENT_ANALYZER) {
    if (syncPending) return 'Basecamp sync is still running — check again after sync finishes.'
    if (linksWithNewDataCount > 0) {
      const n = linksWithNewDataCount
      return `${n} linked client${n === 1 ? '' : 's'} have new Basecamp recordings or comments since the last digest.`
    }
    return 'No new Basecamp activity on digest-enabled client links since the last watermark.'
  }
  if (agentKey === AGENT_TASK_CREATOR) {
    if (badges.includes('ready')) {
      return 'Step 1 produced analyzer output — run this stage to build the follow-up email.'
    }
    if (badges.includes('blocked')) {
      return 'Run the data analyzer (Step 1) first, or choose a saved analyzer run when you open Run.'
    }
  }
  return ''
}

/**
 * @param {boolean} [enabled]
 */
export function useAgentPipelineReadiness(enabled = true) {
  const [state, setState] = useState({ ...EMPTY, loading: enabled })

  const load = useCallback(async () => {
    if (!enabled) {
      setState(EMPTY)
      return
    }
    setState((s) => ({ ...s, loading: true }))
    try {
      const { data: links, error: lErr } = await supabase
        .from('client_basecamp_links')
        .select('id, basecamp_account_id, basecamp_project_id, agents_automation_enabled, agents_last_watermark')
        .eq('agents_automation_enabled', true)
      if (lErr) throw lErr

      const enabledLinks = links || []
      let linksWithNewDataCount = 0
      for (const link of enabledLinks) {
        const has = await linkHasDelta(
          link.basecamp_account_id,
          link.basecamp_project_id,
          link.agents_last_watermark ?? null
        )
        if (has) linksWithNewDataCount += 1
      }

      const { data: batchRow } = await supabase
        .from('basecamp_sync_batch')
        .select('status, started_at')
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      const syncPending = batchRow?.status === 'running'

      const sinceFailed = new Date(Date.now() - FAILED_LOOKBACK_MS).toISOString()
      const { data: recentRuns } = await supabase
        .from('basecamp_agent_runs')
        .select('agent_name, status, finished_at')
        .in('agent_name', [AGENT_ANALYZER, AGENT_TASK_CREATOR])
        .gte('finished_at', sinceFailed)
        .order('finished_at', { ascending: false })
        .limit(20)

      const lastByAgent = {}
      for (const r of recentRuns || []) {
        if (!lastByAgent[r.agent_name]) lastByAgent[r.agent_name] = r
      }

      const { count: analyzerSuccessCount } = await supabase
        .from('basecamp_agent_runs')
        .select('id', { count: 'exact', head: true })
        .eq('agent_name', AGENT_ANALYZER)
        .eq('status', 'success')

      const { data: lastAnalyzer } = await supabase
        .from('basecamp_agent_runs')
        .select('finished_at')
        .eq('agent_name', AGENT_ANALYZER)
        .eq('status', 'success')
        .order('finished_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      const { data: lastTaskCreator } = await supabase
        .from('basecamp_agent_runs')
        .select('finished_at')
        .eq('agent_name', AGENT_TASK_CREATOR)
        .eq('status', 'success')
        .order('finished_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      const analyzerTime = lastAnalyzer?.finished_at ? new Date(lastAnalyzer.finished_at).getTime() : 0
      const taskTime = lastTaskCreator?.finished_at ? new Date(lastTaskCreator.finished_at).getTime() : 0
      const taskCreatorReady = (analyzerSuccessCount ?? 0) > 0 && analyzerTime > taskTime

      /** @type {Record<string, string[]>} */
      const badgesByAgentKey = {}

      const analyzerBadges = []
      if (syncPending) analyzerBadges.push('sync_pending')
      if (linksWithNewDataCount > 0) analyzerBadges.push('new_data')
      if (lastByAgent[AGENT_ANALYZER]?.status === 'error') analyzerBadges.push('failed')
      badgesByAgentKey[AGENT_ANALYZER] = analyzerBadges

      const taskBadges = []
      if (taskCreatorReady) taskBadges.push('ready')
      else if ((analyzerSuccessCount ?? 0) === 0) taskBadges.push('blocked')
      if (lastByAgent[AGENT_TASK_CREATOR]?.status === 'error') taskBadges.push('failed')
      badgesByAgentKey[AGENT_TASK_CREATOR] = taskBadges

      const syncPart = syncPending ? 'Sync in progress' : 'Sync idle'
      const summaryLine = `${syncPart} · ${enabledLinks.length} client link${enabledLinks.length === 1 ? '' : 's'} in digest · ${linksWithNewDataCount} with new data`

      /** @type {Record<string, string>} */
      const statusHintByAgentKey = {}
      for (const key of [AGENT_ANALYZER, AGENT_TASK_CREATOR]) {
        statusHintByAgentKey[key] = buildStatusHint(
          key,
          badgesByAgentKey[key] || [],
          lastByAgent,
          linksWithNewDataCount,
          syncPending
        )
      }

      setState({
        ...EMPTY,
        loading: false,
        linksWithNewDataCount,
        digestEnabledCount: enabledLinks.length,
        syncPending,
        badgesByAgentKey,
        statusHintByAgentKey,
        summaryLine,
      })
    } catch {
      setState({ ...EMPTY, loading: false })
    }
  }, [enabled])

  useEffect(() => {
    void load()
  }, [load])

  return { ...state, reload: load }
}

/** @type {Record<string, { label: string; bg: string; color: string }>} */
export const AGENT_BADGE_STYLES = {
  new_data: { label: 'New data', bg: '#fef3c7', color: '#b45309' },
  sync_pending: { label: 'Sync pending', bg: '#f1f5f9', color: '#475569' },
  ready: { label: 'Ready', bg: '#dbeafe', color: '#1d4ed8' },
  blocked: { label: 'Run step 1', bg: '#f1f5f9', color: '#64748b' },
  failed: { label: 'Failed', bg: '#fee2e2', color: '#b91c1c' },
  inactive: { label: 'Inactive', bg: '#f1f5f9', color: '#94a3b8' },
}
